import type { BusClient } from './bus/client.ts'
import type { FerryClient } from './ferry/client.ts'
import type { MultimodalClient } from './multimodal/client.ts'
import type { TransitClient } from './transit/client.ts'
import type { roadMatrix as RoadMatrix } from './road/client.ts'
import type { bikeShareSnapshot as BikeShareSnapshotLoader } from './bike-share/client.ts'
import { bikeShareTransitEgressCandidateIndexes, exactBikeShareTransitAccessPoints, exactBikeShareTransitEgress, exactBikeShareTransitEgressOptions, geometricBikeShareTransitEgressCandidateIndexes, routeBikeShare } from './bike-share/router.ts'
import { estimatedBicycleTransitAccessPoints, estimatedBikeShareTransitAccessPoints, exactBicycleTransitAccessPoints, prioritizedTransitAccessStations } from './multimodal/router.ts'
import type { ScheduledMode } from './multimodal/data.ts'
import type { ScheduledModeSet } from './multimodal/protocol.ts'
import { isClientIncompatible } from './planner-api.ts'
import { timeSeconds } from './planner-time.ts'
import { distanceKilometres, type LocationPoint, type Venue } from './planning.ts'
import type { StationAccess, TransferPass } from './transit/router.ts'
import { loadScheduledWalkingEgress } from './transit/walking-egress.ts'
import { SearchWalkingAccessCache, type AccessEndpoint } from './walking-access-cache.ts'
import { geometricWalkingEgressCandidateIndexes, isBikeSharePerson, isScheduledPerson, scheduledAlternatives, walkingEgressCandidateIndexes, type EndpointJourney, type RoadMode, type RoutedParticipant, type ScheduledRoutedPerson, type TravelChoice, type TravelMode } from './transit/modes.ts'
import { isSuperseded, type SearchRequest, type SearchRouting, type ScheduledBackend, type ExactTransfersByBackend } from './search.ts'

export type RoutingBackend = ScheduledMode | RoadMode | 'bike-share' | 'multimodal' | 'none'
type RoutingClient<T> = Pick<T, Extract<keyof T, 'metadata' | 'route' | 'arriveBy'>>
export interface SearchRoutingDependencies {
  transit: RoutingClient<TransitClient> & Pick<TransitClient, 'unavailableReason'>
  readyBus(): Promise<RoutingClient<BusClient>>
  readyFerry(): Promise<RoutingClient<FerryClient>>
  readyMultimodal(): Promise<RoutingClient<MultimodalClient>>
  roadMatrix: typeof RoadMatrix
  bikeShareSnapshot: typeof BikeShareSnapshotLoader
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}

export function routingBackend(choice: TravelChoice): RoutingBackend {
  if (choice.kind === 'none') return 'none'
  const scheduled = scheduledModesFor(choice)
  if (scheduled.length) {
    return choice.modes.includes('bike') || choice.modes.includes('bike-share') || scheduled.length > 1
      ? 'multimodal' : scheduled[0]
  }
  if (choice.modes.includes('taxi')) return 'taxi'
  if (choice.modes.includes('bike')) return 'bike'
  if (choice.modes.includes('bike-share')) return 'bike-share'
  return 'none'
}

export function scheduledModesFor(choice: Exclude<TravelChoice, { kind: 'none' }>): ScheduledMode[] {
  return choice.modes.filter((mode): mode is ScheduledMode =>
    mode === 'subway' || mode === 'bus' || mode === 'ferry')
}

export function createSearchRouting(request: SearchRequest, dependencies: SearchRoutingDependencies, signal: AbortSignal): SearchRouting {
  const stop = new AbortController()
  const active = AbortSignal.any([signal, stop.signal])
  const assertActive = () => active.throwIfAborted()
  const rethrowTerminal = (cause: unknown) => {
    if (active.aborted || isClientIncompatible(cause) || isSuperseded(cause)) {
      stop.abort(active.aborted ? active.reason : cause)
      assertActive()
    }
  }
  const transit = dependencies.transit
  let bus: RoutingClient<BusClient> | undefined
  let ferry: RoutingClient<FerryClient> | undefined
  let multimodal: RoutingClient<MultimodalClient> | undefined
  const readyBus = async () => {
    assertActive()
    const client = await dependencies.readyBus()
    assertActive()
    bus = client
    return client
  }
  const readyFerry = async () => {
    assertActive()
    const client = await dependencies.readyFerry()
    assertActive()
    ferry = client
    return client
  }
  const readyMultimodal = async () => {
    assertActive()
    const client = await dependencies.readyMultimodal()
    assertActive()
    multimodal = client
    return client
  }
  const roadMatrix: typeof RoadMatrix = async (...args) => {
    assertActive()
    try {
      const result = await dependencies.roadMatrix(...args)
      assertActive()
      return result
    } catch (cause) {
      rethrowTerminal(cause)
      throw cause
    }
  }
  const bikeShareSnapshot: typeof BikeShareSnapshotLoader = async (...args) => {
    assertActive()
    try {
      const result = await dependencies.bikeShareSnapshot(...args)
      assertActive()
      return result
    } catch (cause) {
      rethrowTerminal(cause)
      throw cause
    }
  }
  const points = request.participants.map(({ point }) => point)
  const origins = request.participants.map(({ modes }) => ({ choice: { kind: 'permissions' as const, modes } }))
  const walkingAccess = new SearchWalkingAccessCache()
  const arrivalSeconds = request.time.intent === 'arrive-by' ? timeSeconds(request.time.time) : undefined
  let lastBikeShareUpdatedAt: number | null = null
  async function routeSelectedTransfers(
    areas: Array<{ journeys?: EndpointJourney[] }>,
    scheduledPeople: ScheduledRoutedPerson[],
  ): Promise<ExactTransfersByBackend> {
    assertActive()
    const candidates = new Map<string, {
      backend: ScheduledBackend
      transferIndex: number
      from: number
      to: number
      source: LocationPoint
      destination: Venue
    }>()
    for (const area of areas) {
      area.journeys?.forEach((journey, personIndex) => {
        const person = scheduledPeople[personIndex]
        if (!person || journey.kind !== 'scheduled') return
        const backend = scheduledBackend(person)
        for (const leg of journey.journey.legs) {
          if (leg.kind !== 'transfer' || leg.generatedTransferIndex === undefined) continue
          const from = person.endpoints[leg.fromStation]
          const to = person.endpoints[leg.toStation]
          if (!from || !to) continue
          const key = `${backend}:${leg.generatedTransferIndex}`
          if (!candidates.has(key)) candidates.set(key, {
            backend,
            transferIndex: leg.generatedTransferIndex,
            from: leg.fromStation,
            to: leg.toStation,
            source: { displayName: from[1], lat: from[2], lon: from[3], type: 'transit-transfer' },
            destination: { id: key, name: to[1], lat: to[2], lon: to[3], type: 'transit-transfer' },
          })
        }
      })
    }
    const selected = [...candidates.values()].slice(0, 32)
    if (!selected.length) return {}
    try {
      const sources = [...new Map(selected.map((candidate) => [
        `${candidate.backend}:${candidate.from}`, candidate.source,
      ])).entries()]
      const destinations = [...new Map(selected.map((candidate) => [
        `${candidate.backend}:${candidate.to}`, candidate.destination,
      ])).entries()]
      const durations = await roadMatrix(
        'foot', sources.map(([, point]) => point), destinations.map(([, point]) => point),
      )
      assertActive()
      const sourceIndexes = new Map(sources.map(([key], index) => [key, index]))
      const destinationIndexes = new Map(destinations.map(([key], index) => [key, index]))
      const exact: ExactTransfersByBackend = {}
      for (const candidate of selected) {
        const seconds = durations[sourceIndexes.get(`${candidate.backend}:${candidate.from}`)!]
          [destinationIndexes.get(`${candidate.backend}:${candidate.to}`)!]
        if (seconds === null) continue
        ;(exact[candidate.backend] ??= []).push([
          candidate.transferIndex, candidate.from, candidate.to, seconds,
        ])
      }
      return exact
    } catch (cause) {
      rethrowTerminal(cause)
      if (errorMessage(cause).startsWith('Superseded')) throw cause
      return {}
    }
  }

  function scheduledBackend(person: ScheduledRoutedPerson): ScheduledBackend {
    if (person.endpoints === transit.metadata?.stations) return 'subway'
    if (person.endpoints === bus?.metadata?.stops) return 'bus'
    if (person.endpoints === ferry?.metadata?.terminals) return 'ferry'
    return 'multimodal'
  }

  async function exactWalkingAccess(
    points: LocationPoint[],
    endpoints: AccessEndpoint[],
    modesByPerson: ScheduledModeSet[],
  ): Promise<StationAccess[][]> {
    const limits: Record<ScheduledMode, { distanceKm: number; count: number; boardingSeconds: number }> = {
      subway: { distanceKm: 1.2, count: 5, boardingSeconds: 0 },
      bus: { distanceKm: 0.8, count: 12, boardingSeconds: 60 },
      ferry: { distanceKm: 1.2, count: 8, boardingSeconds: 10 * 60 },
    }
    const candidates = points.map((point, personIndex) => modesByPerson[personIndex].flatMap((mode) => {
      const limit = limits[mode]
      return endpoints.flatMap((endpoint, stationIndex) => (endpoint[4] ?? mode) === mode ? [{
        stationIndex,
        distance: distanceKilometres(point, { lat: endpoint[2], lon: endpoint[3] }),
        boardingSeconds: limit.boardingSeconds,
      }] : []).filter(({ distance }) => distance <= limit.distanceKm)
        .sort((left, right) => left.distance - right.distance || left.stationIndex - right.stationIndex)
        .slice(0, limit.count)
    }))
    const selectedIndexes: number[] = []
    const selected = new Set<number>()
    const cursors = candidates.map(() => 0)
    while (selectedIndexes.length < 40) {
      let added = false
      for (const [streamIndex, stream] of candidates.entries()) {
        while (cursors[streamIndex] < stream.length
          && selected.has(stream[cursors[streamIndex]].stationIndex)) cursors[streamIndex] += 1
        const candidate = stream[cursors[streamIndex]]
        if (!candidate) continue
        selected.add(candidate.stationIndex)
        selectedIndexes.push(candidate.stationIndex)
        cursors[streamIndex] += 1
        added = true
        if (selectedIndexes.length === 40) break
      }
      if (!added) break
    }
    if (!selectedIndexes.length) return points.map(() => [])
    const destinations: Venue[] = selectedIndexes.map((stationIndex) => {
      const endpoint = endpoints[stationIndex]
      return { id: endpoint[0], name: endpoint[1], lat: endpoint[2], lon: endpoint[3], type: 'transit-stop' }
    })
    const durations = await roadMatrix('foot', points, destinations)
    assertActive()
    const columns = new Map(selectedIndexes.map((stationIndex, column) => [stationIndex, column]))
    return candidates.map((personCandidates, personIndex) => personCandidates.flatMap((candidate) => {
      const column = columns.get(candidate.stationIndex)
      const seconds = column === undefined ? null : durations[personIndex][column]
      return seconds === null || seconds === undefined ? [] : [{
        stationIndex: candidate.stationIndex,
        mode: 'walk' as const,
        travelSeconds: seconds,
        seconds: seconds + candidate.boardingSeconds,
      }]
    }))
  }

  async function routeGroup(
    transferPass: TransferPass,
    exactTransfers: ExactTransfersByBackend,
  ): Promise<RoutedParticipant[]> {
    assertActive()
    const indexesFor = (backend: RoutingBackend) => origins.flatMap((origin, index) =>
      routingBackend(origin.choice) === backend ? [index] : [])
    const subwayIndexes = indexesFor('subway')
    const busIndexes = indexesFor('bus')
    const multimodalIndexes = indexesFor('multimodal')
    const ferryIndexes = indexesFor('ferry')
    const routed: RoutedParticipant[] = origins.map((origin, index) => {
      if (origin.choice.kind !== 'permissions') throw new Error('Expected travel permissions.')
      return {
        permissions: origin.choice.modes,
        alternatives: [
          ...(origin.choice.modes.includes('walk') ? [{ kind: 'walk' as const, origin: points[index] }] : []),
          ...(origin.choice.modes.includes('taxi') ? [{ mode: 'taxi' as const, durations: [] }] : []),
          ...(origin.choice.modes.includes('bike') ? [{ mode: 'bike' as const, durations: [] }] : []),
          ...(origin.choice.modes.includes('bike-share')
            ? [{ kind: 'bike-share' as const, journeys: [], snapshotUpdatedAt: 0 }] : []),
        ],
        failures: [],
      }
    })
    const scheduledAccessByPerson = new Map<number, StationAccess[]>()
    const recordFailure = (indexes: number[], mode: TravelMode, cause: unknown) => {
      rethrowTerminal(cause)
      if (errorMessage(cause).startsWith('Superseded')) throw cause
      indexes.forEach((index) => routed[index].failures.push({ mode, message: errorMessage(cause) }))
    }
    const recordScheduledFailure = (indexes: number[], cause: unknown) => {
      for (const index of indexes) {
        const choice = origins[index].choice
        if (choice.kind !== 'permissions') continue
        for (const mode of scheduledModesFor(choice)) recordFailure([index], mode, cause)
      }
    }
    if (subwayIndexes.length && !transit.metadata) {
      recordFailure(subwayIndexes, 'subway', transit.unavailableReason ?? 'Subway schedules are unavailable.')
    }
    try {
      const hasScheduledRoutes = Boolean(subwayIndexes.length || busIndexes.length
        || multimodalIndexes.length || ferryIndexes.length)
      const date = request.discoveryTime.date
      const [hours, minutes] = request.discoveryTime.time.split(':').map(Number)
      const scheduleAvailable = request.scheduleAvailable
      if (!scheduleAvailable) {
        const message = new Error('No schedule date is available for the selected travel modes.')
        if (subwayIndexes.length) recordFailure(subwayIndexes, 'subway', message)
        if (busIndexes.length) recordFailure(busIndexes, 'bus', message)
        if (ferryIndexes.length) recordFailure(ferryIndexes, 'ferry', message)
        if (multimodalIndexes.length) recordScheduledFailure(multimodalIndexes, message)
      }
      const departureSeconds = hasScheduledRoutes ? hours * 3600 + minutes * 60 : 0
      await Promise.all([
        subwayIndexes.length && transit.metadata && scheduleAvailable ? walkingAccess.get(
          'subway',
          subwayIndexes.map((index) => points[index]),
          transit.metadata.stations,
          subwayIndexes.map(() => ['subway']),
          () => exactWalkingAccess(
            subwayIndexes.map((index) => points[index]),
            transit.metadata!.stations,
            subwayIndexes.map(() => ['subway']),
          ),
        ).then((walkingAccessByOrigin) => {
          assertActive()
          walkingAccessByOrigin.forEach((access, index) => {
            scheduledAccessByPerson.set(subwayIndexes[index], access)
          })
          assertActive()
          return transit.route(date, departureSeconds, subwayIndexes.map((index) => points[index]), {
            walkingAccessByOrigin, transferPass, exactTransfers: exactTransfers.subway,
          })
        }).then((results) => results.forEach((result, resultIndex) => {
          routed[subwayIndexes[resultIndex]].alternatives.push({
            allowedModes: ['subway'],
            feedVersion: transit.metadata!.feedVersion,
            access: scheduledAccessByPerson.get(subwayIndexes[resultIndex]),
            exactTransfers: exactTransfers.subway,
            result,
            endpoints: transit.metadata!.stations,
            routes: transit.metadata!.routes,
          })
        })).catch((cause) => recordFailure(subwayIndexes, 'subway', cause)) : Promise.resolve(),
        busIndexes.length && scheduleAvailable ? readyBus().then(async (client) => {
          assertActive()
          const busPoints = busIndexes.map((index) => points[index])
          const walkingAccessByOrigin = await walkingAccess.get(
            'bus', busPoints, client.metadata!.stops, busPoints.map(() => ['bus']),
            () => exactWalkingAccess(busPoints, client.metadata!.stops, busPoints.map(() => ['bus'])),
          )
          assertActive()
          walkingAccessByOrigin.forEach((access, index) => {
            scheduledAccessByPerson.set(busIndexes[index], access)
          })
          assertActive()
          return client.route(date, departureSeconds, busPoints, {
            walkingAccessByOrigin, transferPass, exactTransfers: exactTransfers.bus,
          })
        }).then((results) => results.forEach((result, resultIndex) => {
          routed[busIndexes[resultIndex]].alternatives.push({
            allowedModes: ['bus'],
            feedVersion: bus!.metadata!.feedVersion,
            access: scheduledAccessByPerson.get(busIndexes[resultIndex]),
            exactTransfers: exactTransfers.bus,
            result,
            endpoints: bus!.metadata!.stops,
            routes: bus!.metadata!.routes,
          })
        })).catch((cause) => recordFailure(busIndexes, 'bus', cause)) : Promise.resolve(),
        ferryIndexes.length && scheduleAvailable ? readyFerry().then(async (client) => {
          assertActive()
          const ferryPoints = ferryIndexes.map((index) => points[index])
          const walkingAccessByOrigin = await walkingAccess.get(
            'ferry', ferryPoints, client.metadata!.terminals, ferryPoints.map(() => ['ferry']),
            () => exactWalkingAccess(ferryPoints, client.metadata!.terminals, ferryPoints.map(() => ['ferry'])),
          )
          assertActive()
          walkingAccessByOrigin.forEach((access, index) => {
            scheduledAccessByPerson.set(ferryIndexes[index], access)
          })
          assertActive()
          return client.route(date, departureSeconds, ferryPoints, {
            walkingAccessByOrigin, transferPass, exactTransfers: exactTransfers.ferry,
          })
        }).then((results) => results.forEach((result, resultIndex) => {
          routed[ferryIndexes[resultIndex]].alternatives.push({
            allowedModes: ['ferry'],
            feedVersion: ferry!.metadata!.feedVersion,
            access: scheduledAccessByPerson.get(ferryIndexes[resultIndex]),
            exactTransfers: exactTransfers.ferry,
            result,
            endpoints: ferry!.metadata!.terminals,
            routes: ferry!.metadata!.routes,
          })
        })).catch((cause) => recordFailure(ferryIndexes, 'ferry', cause)) : Promise.resolve(),
        multimodalIndexes.length && scheduleAvailable ? readyMultimodal().then(async (client) => {
          assertActive()
          const multimodalPoints = multimodalIndexes.map((index) => points[index])
          const choices = multimodalIndexes.map((index) => {
            const choice = origins[index].choice
            if (choice.kind !== 'permissions') throw new Error('Expected travel permissions.')
            return {
              modes: scheduledModesFor(choice) as ScheduledModeSet,
              bicycleAccess: choice.modes.includes('bike'),
              bikeShareAccess: choice.modes.includes('bike-share'),
            }
          })
          const walkingAccessByOrigin = await walkingAccess.get(
            'multimodal',
            multimodalPoints,
            client.metadata!.nodes,
            choices.map((choice) => choice.modes),
            () => exactWalkingAccess(
              multimodalPoints,
              client.metadata!.nodes,
              choices.map((choice) => choice.modes),
            ),
          )
          assertActive()
          const routeOptions = {
            walkingAccessByOrigin,
            transferPass,
            exactTransfers: exactTransfers.multimodal,
          }
          const bicycleEnabled = choices.map((choice) => Boolean(choice.bicycleAccess))
          const bikeShareEnabled = choices.map((choice) => Boolean(choice.bikeShareAccess))
          if (![...bicycleEnabled, ...bikeShareEnabled].some(Boolean)) {
            assertActive()
            walkingAccessByOrigin.forEach((access, index) => {
              scheduledAccessByPerson.set(multimodalIndexes[index], access)
            })
            assertActive()
            return client.route(
              date,
              departureSeconds,
              multimodalPoints,
              choices.map((choice) => choice.modes),
              [],
              false,
              routeOptions,
            )
          }
          const bicycleEstimatedAccess = estimatedBicycleTransitAccessPoints(
            client.metadata!.nodes,
            multimodalPoints,
            choices.map((choice) => choice.modes),
            bicycleEnabled,
          )
          const bikeShareEstimatedAccess = estimatedBikeShareTransitAccessPoints(
            client.metadata!.nodes,
            multimodalPoints,
            choices.map((choice) => choice.modes),
            bikeShareEnabled,
          )
          const estimatedAccess = multimodalPoints.map((_, index) => [
            ...bicycleEstimatedAccess[index],
            ...bikeShareEstimatedAccess[index],
          ])
          assertActive()
          const preliminary = await client.route(
            date,
            departureSeconds,
            multimodalPoints,
            choices.map((choice) => choice.modes),
            estimatedAccess,
            true,
            routeOptions,
          )
          assertActive()
          const candidateStations = prioritizedTransitAccessStations(
            client.metadata!.nodes, estimatedAccess, preliminary,
          )
          const exactAccess: StationAccess[][] = multimodalPoints.map(() => [])
          if (bicycleEnabled.some(Boolean)) {
            try {
              const bicycleAccess = await exactBicycleTransitAccessPoints(
                client.metadata!.nodes,
                multimodalPoints,
                choices.map((choice) => choice.modes),
                bicycleEnabled,
                candidateStations,
                roadMatrix,
              )
              assertActive()
              bicycleAccess.forEach((access, index) => exactAccess[index].push(...access))
            } catch (cause) {
              recordFailure(multimodalIndexes.filter((_, index) => bicycleEnabled[index]), 'bike', cause)
            }
          }
          assertActive()
          if (bikeShareEnabled.some(Boolean)) {
            try {
              const snapshot = await bikeShareSnapshot([
                ...multimodalPoints.filter((_, index) => bikeShareEnabled[index]),
                ...candidateStations.map((stationIndex) => ({
                  lat: client.metadata!.nodes[stationIndex][2],
                  lon: client.metadata!.nodes[stationIndex][3],
                })),
              ])
              assertActive()
              lastBikeShareUpdatedAt = snapshot.updatedAt
              const bikeShareAccess = await exactBikeShareTransitAccessPoints(
                client.metadata!.nodes,
                multimodalPoints,
                choices.map((choice) => choice.modes),
                bikeShareEnabled,
                candidateStations,
                snapshot,
                roadMatrix,
              )
              assertActive()
              bikeShareAccess.forEach((access, index) => exactAccess[index].push(...access))
            } catch (cause) {
              recordFailure(multimodalIndexes.filter((_, index) => bikeShareEnabled[index]), 'bike-share', cause)
            }
          }
          assertActive()
          walkingAccessByOrigin.forEach((access, index) => {
            scheduledAccessByPerson.set(multimodalIndexes[index], [...access, ...exactAccess[index]])
          })
          assertActive()
          return client.route(
            date,
            departureSeconds,
            multimodalPoints,
            choices.map((choice) => choice.modes),
            exactAccess,
            false,
            routeOptions,
          )
        }).then((results) => results.forEach((result, resultIndex) => {
          const personIndex = multimodalIndexes[resultIndex]
          const choice = origins[personIndex].choice
          if (choice.kind !== 'permissions') return
          routed[multimodalIndexes[resultIndex]].alternatives.push({
            allowedModes: scheduledModesFor(choice) as ScheduledModeSet,
            feedVersion: multimodal!.metadata!.feedVersion,
            bicycleAccess: choice.modes.includes('bike'),
            bikeShareAccess: choice.modes.includes('bike-share'),
            access: scheduledAccessByPerson.get(personIndex),
            exactTransfers: exactTransfers.multimodal,
            result,
            endpoints: multimodal!.metadata!.nodes,
            routes: multimodal!.metadata!.routes,
          })
        })).catch((cause) => recordScheduledFailure(multimodalIndexes, cause)) : Promise.resolve(),
      ])
      assertActive()
      return routed
    } catch (cause) {
      rethrowTerminal(cause)
      if (errorMessage(cause).startsWith('Superseded')) throw cause
      throw cause
    }
  }

  async function routeVenuePeople(
    people: RoutedParticipant[],
    venues: Venue[],
  ): Promise<RoutedParticipant[]> {
    assertActive()
    if (!venues.length) return people
    const completed = people.map((person) => ({
      ...person,
      ...(arrivalSeconds === undefined ? {} : { arrivalSeconds }),
      alternatives: [...person.alternatives],
      failures: [...person.failures],
    }))
    const recordFailure = (indexes: number[], mode: TravelMode, cause: unknown) => {
      rethrowTerminal(cause)
      assertActive()
      if (errorMessage(cause).startsWith('Superseded')) throw cause
      indexes.forEach((index) => completed[index].failures.push({ mode, message: errorMessage(cause) }))
    }
    const walkingIndexes = people.flatMap((person, index) =>
      person.permissions.includes('walk') ? [index] : [])
    if (walkingIndexes.length) {
      try {
        const matrix = await roadMatrix(
          'foot', walkingIndexes.map((index) => points[index]), venues,
        )
        assertActive()
        matrix.forEach((durations, row) => {
          const alternative = completed[walkingIndexes[row]].alternatives.find((candidate) =>
            'origin' in candidate)
          if (alternative && 'origin' in alternative) alternative.durations = durations
        })
      } catch (cause) {
        recordFailure(walkingIndexes, 'walk', cause)
        walkingIndexes.forEach((index) => {
          completed[index].alternatives = completed[index].alternatives.filter((alternative) =>
            !('origin' in alternative))
        })
      }
    }
    for (const mode of ['taxi', 'bike'] as const) {
      const indexes = people.flatMap((person, index) => person.permissions.includes(mode) ? [index] : [])
      if (!indexes.length) continue
      try {
        const matrix = await roadMatrix(
          mode === 'taxi' ? 'car' : 'bike',
          indexes.map((index) => points[index]),
          venues,
        )
        assertActive()
        matrix.forEach((durations, row) => {
          const person = completed[indexes[row]]
          const alternativeIndex = person.alternatives.findIndex((alternative) =>
            !('origin' in alternative) && !isScheduledPerson(alternative)
            && !isBikeSharePerson(alternative) && alternative.mode === mode)
          person.alternatives[alternativeIndex] = {
            mode,
            durations: durations.map((duration) => duration === null
              ? null
              : duration + (mode === 'taxi' ? 300 : 0)),
          }
        })
      } catch (cause) {
        recordFailure(indexes, mode, cause)
        indexes.forEach((index) => {
          completed[index].alternatives = completed[index].alternatives.filter((alternative) =>
            'origin' in alternative || isScheduledPerson(alternative) || isBikeSharePerson(alternative)
              || alternative.mode !== mode)
        })
      }
    }
    const scheduledEgressInputs = completed.flatMap((participant, participantIndex) =>
      scheduledAlternatives(participant).map((person) => ({
        participantIndex,
        person,
        candidatesByVenue: venues.map((venue) => arrivalSeconds === undefined
          ? walkingEgressCandidateIndexes(person, venue, 1)
          : geometricWalkingEgressCandidateIndexes(person, venue, 2)),
      })))
    const scheduledEgressFailures = await loadScheduledWalkingEgress(
      scheduledEgressInputs,
      venues,
      roadMatrix,
      () => {
        assertActive()
      },
    )
    assertActive()
    for (const { inputs, cause } of scheduledEgressFailures) {
      rethrowTerminal(cause)
      for (const { participantIndex, person } of inputs) {
        person.walkingEgressByVenue = venues.map(() => [])
        for (const mode of person.allowedModes) recordFailure([participantIndex], mode, cause)
      }
    }
    const bikeShareIndexes = people.flatMap((person, index) =>
      person.permissions.includes('bike-share') ? [index] : [])
    if (bikeShareIndexes.length) {
      try {
        const bikeShareOrigins = bikeShareIndexes.map((index) => points[index])
        const snapshot = await bikeShareSnapshot([...bikeShareOrigins, ...venues])
        assertActive()
        lastBikeShareUpdatedAt = snapshot.updatedAt
        const routed = await routeBikeShare(bikeShareOrigins, venues, snapshot, roadMatrix)
        assertActive()
        routed.forEach((alternative, index) => {
          const person = completed[bikeShareIndexes[index]]
          const alternativeIndex = person.alternatives.findIndex((candidate) =>
            !('origin' in candidate) && isBikeSharePerson(candidate))
          person.alternatives[alternativeIndex] = alternative
        })
      } catch (cause) {
        recordFailure(bikeShareIndexes, 'bike-share', cause)
        bikeShareIndexes.forEach((index) => {
          completed[index].alternatives = completed[index].alternatives.filter((alternative) =>
            'origin' in alternative || !isBikeSharePerson(alternative))
        })
      }
    }
    const bikeShareEgressIndexes = people.flatMap((person, index) =>
      scheduledAlternatives(person).some((alternative) => alternative.bikeShareAccess) ? [index] : [])
    if (bikeShareEgressIndexes.length) {
      try {
        const nodes = multimodal?.metadata?.nodes
        if (!nodes) throw new Error('Multimodal routing metadata is unavailable for bike-share egress.')
        const bikeSharePeople = bikeShareEgressIndexes.map((index) =>
          scheduledAlternatives(completed[index]).find((alternative) => alternative.bikeShareAccess)!)
        const candidateIndexes = arrivalSeconds === undefined
          ? bikeShareTransitEgressCandidateIndexes(nodes, bikeSharePeople, venues)
          : geometricBikeShareTransitEgressCandidateIndexes(
              nodes, venues, [...new Set(bikeSharePeople.flatMap(({ allowedModes }) => allowedModes))],
            )
        if (candidateIndexes.length) {
          const snapshot = await bikeShareSnapshot([
            ...candidateIndexes.map((stationIndex) => ({ lat: nodes[stationIndex][2], lon: nodes[stationIndex][3] })),
            ...venues,
          ])
          assertActive()
          lastBikeShareUpdatedAt = snapshot.updatedAt
          if (arrivalSeconds === undefined) {
            const egress = await exactBikeShareTransitEgress(
              nodes, bikeSharePeople, venues, candidateIndexes, snapshot, roadMatrix,
            )
            assertActive()
            bikeShareEgressIndexes.forEach((personIndex, index) => {
              const person = completed[personIndex]
              const alternativeIndex = person.alternatives.indexOf(bikeSharePeople[index])
              person.alternatives[alternativeIndex] = {
                ...bikeSharePeople[index], bikeShareEgressByVenue: egress[index],
              }
            })
          } else {
            const egress = await exactBikeShareTransitEgressOptions(
              nodes, bikeSharePeople, venues, candidateIndexes, snapshot, roadMatrix,
            )
            assertActive()
            bikeShareEgressIndexes.forEach((personIndex, index) => {
              const person = completed[personIndex]
              const alternativeIndex = person.alternatives.indexOf(bikeSharePeople[index])
              person.alternatives[alternativeIndex] = {
                ...bikeSharePeople[index], bikeShareEgressOptionsByVenue: egress[index],
              }
            })
          }
        }
      } catch (cause) {
        recordFailure(bikeShareEgressIndexes, 'bike-share', cause)
      }
    }
    if (arrivalSeconds !== undefined) {
      await routeArriveByScheduledPeople(completed, venues, arrivalSeconds, recordFailure)
    }
    assertActive()
    return completed
  }

  async function routeArriveByScheduledPeople(
    people: RoutedParticipant[],
    venues: Venue[],
    arrivalSeconds: number,
    recordFailure: (indexes: number[], mode: TravelMode, cause: unknown) => void,
  ): Promise<void> {
    assertActive()
    const date = request.time.date
    for (const [personIndex, participant] of people.entries()) {
      for (const person of scheduledAlternatives(participant)) {
        assertActive()
        const access = person.access
        if (!access?.length) {
          person.arriveByJourneys = venues.map(() => null)
          continue
        }
        const egressByDestination = venues.map((_, venueIndex) => [
          ...(person.walkingEgressByVenue?.[venueIndex] ?? []).map(({ endpointIndex, seconds }) => ({
            stationIndex: endpointIndex, seconds,
          })),
          ...(person.bikeShareEgressOptionsByVenue?.[venueIndex] ?? []).map((egress) => ({
            stationIndex: egress.stationIndex, seconds: egress.seconds,
          })),
        ])
        try {
          const options = { transferPass: 'final' as const, exactTransfers: person.exactTransfers }
          const backend = scheduledBackend(person)
          let results
          if (backend === 'subway') {
            assertActive()
            results = await transit.arriveBy(date, arrivalSeconds, [access], egressByDestination, options)
          } else if (backend === 'bus') {
            const client = await readyBus()
            assertActive()
            results = await client.arriveBy(date, arrivalSeconds, [access], egressByDestination, options)
          } else if (backend === 'ferry') {
            const client = await readyFerry()
            assertActive()
            results = await client.arriveBy(date, arrivalSeconds, [access], egressByDestination, options)
          } else {
            const client = await readyMultimodal()
            assertActive()
            results = await client.arriveBy(
              date, arrivalSeconds, [access], egressByDestination, [person.allowedModes], options,
            )
          }
          assertActive()
          person.arriveByJourneys = results.map((destination) => destination[0] ?? null)
        } catch (cause) {
          rethrowTerminal(cause)
          if (errorMessage(cause).startsWith('Superseded')) throw cause
          person.arriveByJourneys = venues.map(() => null)
          for (const mode of person.allowedModes) recordFailure([personIndex], mode, cause)
        }
      }
    }
  }

  return { routeGroup, routeSelectedTransfers, routeVenuePeople, get bikeShareUpdatedAt() { return lastBikeShareUpdatedAt } }
}
